import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy } from 'react';
import ProtectedRoute from '@/app/common/components/guards/ProtectedRoute.tsx';
import SuperAdminRoute from '@/app/common/components/guards/SuperAdminRoute.tsx';
import AdminRoute from '@/app/common/components/guards/AdminRoute.tsx';
import EditorRoute from '@/app/common/components/guards/EditorRoute.tsx';
import PublicRoute from '@/app/common/components/guards/PublicRoute.tsx';
import CatchAllRedirect from '@/app/common/components/guards/CatchAllRedirect.tsx';

const AppLayout = lazy(() => import('@/app/common/components/layout/AppLayout.tsx'));
const MainLayout = lazy(() => import('@/app/common/components/layout/MainLayout.tsx'));
const InternalLayout = lazy(() => import('@/app/common/components/layout/InternalLayout.tsx'));
const SettingsLayout = lazy(() => import('@/app/features/account/components/SettingsLayout.tsx'));

const LoginPage = lazy(() => import('@/app/features/auth/LoginPage.tsx'));
const SignupPage = lazy(() => import('@/app/features/auth/SignupPage.tsx'));
const SignupWithGooglePage = lazy(() => import('@/app/features/auth/SignupWithGooglePage.tsx'));
const ForgotPasswordPage = lazy(() => import('@/app/features/auth/ForgotPasswordPage.tsx'));
const ResetPasswordPage = lazy(() => import('@/app/features/auth/ResetPasswordPage.tsx'));
const NoAccountPage = lazy(() => import('@/app/features/auth/NoAccountPage.tsx'));
const UnsubscribePage = lazy(() => import('@/app/features/auth/UnsubscribePage.tsx'));
const DemoLoginPage = lazy(() => import('@/app/features/auth/DemoLoginPage.tsx'));

const TestPage = lazy(() => import('@/app/features/test/TestPage.tsx'));
const DashboardPage = lazy(() => import('@/app/features/dashboard/DashboardPage.tsx'));
const ProfilePage = lazy(() => import('@/app/features/user/ProfilePage.tsx'));
const ChangePasswordPage = lazy(() => import('@/app/features/user/ChangePasswordPage.tsx'));
const SwitchAccountPage = lazy(() => import('@/app/features/user/SwitchAccountPage.tsx'));

const SubscriptionPage = lazy(() => import('@/app/features/subscription/SubscriptionPage.tsx'));
const SubscriptionSettingsPage = lazy(() => import('@/app/features/subscription/SubscriptionSettingsPage.tsx'));
const CheckoutCompletePage = lazy(() => import('@/app/features/subscription/CheckoutCompletePage.tsx'));
const CheckoutCancelPage = lazy(() => import('@/app/features/subscription/CheckoutCancelPage.tsx'));
const AccountSettingsPage = lazy(() => import('@/app/features/account/AccountSettingsPage.tsx'));
const AccountUsersPage = lazy(() => import('@/app/features/account/settings/users/AccountUsersPage.tsx'));
const InviteUserPage = lazy(() => import('@/app/features/account/settings/users/InviteUserPage.tsx'));
const EditUserPage = lazy(() => import('@/app/features/account/settings/users/EditUserPage.tsx'));
const AcceptInvitePage = lazy(() => import('@/app/features/auth/AcceptInvitePage.tsx'));
const SubjectsListPage = lazy(() => import('@/app/features/subjects/SubjectsListPage.tsx'));
const AddSubjectPage = lazy(() => import('@/app/features/subjects/AddSubjectPage.tsx'));
const SubjectDetailPage = lazy(() => import('@/app/features/subjects/SubjectDetailPage.tsx'));
const SubjectChecksPage = lazy(() => import('@/app/features/subject-checks/SubjectChecksPage.tsx'));
const SubjectCheckDetailPage = lazy(() => import('@/app/features/subject-checks/SubjectCheckDetailPage.tsx'));
const ChangesPage = lazy(() => import('@/app/features/changes/ChangesPage.tsx'));
const SubjectChangeDetailPage = lazy(() => import('@/app/features/changes/SubjectChangeDetailPage.tsx'));
const ContactsPage = lazy(() => import('@/app/features/contacts/ContactsPage.tsx'));
const ContactFormPage = lazy(() => import('@/app/features/contacts/ContactFormPage.tsx'));
const IsirSearchPage = lazy(() => import('@/app/features/search/IsirSearchPage.tsx'));
const AdminStatsPage = lazy(() => import('@/app/features/admin/AdminStatsPage.tsx'));
const CheckLogsPage = lazy(() => import('@/app/features/admin/CheckLogsPage.tsx'));
const AdminAccountsPage = lazy(() => import('@/app/features/internal/AdminAccountsPage.tsx'));

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <PublicRoute><LoginPage /></PublicRoute>,
  },
  {
    path: '/signup',
    element: <PublicRoute><SignupPage /></PublicRoute>,
  },
  {
    path: '/signup-with-google',
    element: <PublicRoute><SignupWithGooglePage /></PublicRoute>,
  },
  {
    path: '/forgot-password',
    element: <PublicRoute><ForgotPasswordPage /></PublicRoute>,
  },
  {
    path: '/reset-password',
    element: <PublicRoute><ResetPasswordPage /></PublicRoute>,
  },
  {
    path: '/no-account',
    element: <NoAccountPage />,
  },
  {
    path: '/unsubscribe',
    element: <UnsubscribePage />,
  },
  {
    path: '/demo',
    element: <DemoLoginPage />,
  },
  {
    path: '/test-page',
    element: <TestPage />,
  },
  {
    path: '/',
    element: <ProtectedRoute><AppLayout /></ProtectedRoute>,
    children: [
      {
        element: <MainLayout />,
        children: [
          { index: true, element: <Navigate to="home" replace /> },
          { path: 'home', element: <DashboardPage /> },
          { path: 'subjects', element: <SubjectsListPage /> },
          { path: 'subjects/add', element: <EditorRoute><AddSubjectPage /></EditorRoute> },
          { path: 'subjects/:id/:tab?', element: <SubjectDetailPage /> },
          { path: 'checks', element: <SubjectChecksPage /> },
          { path: 'checks/:id', element: <SubjectCheckDetailPage /> },
          { path: 'changes', element: <ChangesPage /> },
          { path: 'changes/:id', element: <SubjectChangeDetailPage /> },
          { path: 'contacts', element: <ContactsPage /> },
          { path: 'contacts/add', element: <EditorRoute><ContactFormPage /></EditorRoute> },
          { path: 'contacts/:id/edit', element: <EditorRoute><ContactFormPage /></EditorRoute> },
          { path: 'search', element: <IsirSearchPage /> },
          { path: 'user/profile', element: <ProfilePage /> },
          { path: 'user/change-password', element: <ChangePasswordPage /> },
          { path: 'user/switch-account', element: <SwitchAccountPage /> },
          { path: 'subscription', element: <SubscriptionPage /> },
        ],
      },
      {
        path: 'account/settings',
        element: <AdminRoute><SettingsLayout /></AdminRoute>,
        children: [
          { index: true, element: <AccountSettingsPage /> },
          { path: 'users', element: <AdminRoute><AccountUsersPage /></AdminRoute> },
          { path: 'users/invite', element: <AdminRoute><InviteUserPage /></AdminRoute> },
          { path: 'users/edit/:id', element: <AdminRoute><EditUserPage /></AdminRoute> },
          { path: 'subscription', element: <AdminRoute><SubscriptionSettingsPage /></AdminRoute> },
          { path: 'subscription/checkout-complete', element: <CheckoutCompletePage /> },
          { path: 'subscription/checkout-cancel', element: <CheckoutCancelPage /> },
        ],
      },
      {
        path: 'internal',
        element: <SuperAdminRoute><InternalLayout /></SuperAdminRoute>,
        children: [
          { index: true, element: <AdminStatsPage /> },
          { path: 'accounts', element: <AdminAccountsPage /> },
          { path: 'check-logs', element: <CheckLogsPage /> },
        ],
      },
    ],
  },
  {
    path: '/accept-invite',
    element: <AcceptInvitePage />,
  },
  {
    path: '*',
    element: <CatchAllRedirect />,
  },
]);
